import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useAppVersion from '../hooks/useAppVersion';

/**
 * Estrategia de actualización cuando se detecta un deploy nuevo:
 *
 *   'auto'   → avisa con el banner y recarga sola en el primer momento seguro
 *              (al navegar a otra pantalla, o al volver a la pestaña tras un
 *              rato fuera). Es la recomendada: nadie pierde lo que estaba
 *              haciendo y aun así todos terminan en la versión nueva.
 *   'prompt' → solo avisa; el usuario decide cuándo recargar.
 *   'force'  → recarga en cuanto detecta la versión nueva, sin preguntar.
 */
const UPDATE_STRATEGY = 'auto';

// Rutas donde nunca se recarga sola: el usuario perdería datos capturados.
const BLOCKED_PATHS = ['/comprar-clases/checkout', '/auth/signup', '/auth/nueva-contraseña'];

// Minutos que la pestaña debe haber estado en segundo plano para que, al
// volver, se considere seguro recargar aunque el usuario no navegue.
const IDLE_RELOAD_MS = 5 * 60 * 1000;

export default function UpdateGate() {
  const { updateAvailable, latestVersion, check } = useAppVersion();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const pathAtDetection = useRef(null);
  const hiddenSince = useRef(null);

  const blocked = BLOCKED_PATHS.some((p) => location.pathname.startsWith(p));

  const reload = () => window.location.reload();

  // Revisa también al cambiar de pantalla: hace la detección mucho más rápida
  // que esperar al siguiente ciclo del intervalo.
  useEffect(() => {
    check();
  }, [location.pathname, check]);

  // Recarga automática en el primer momento seguro.
  useEffect(() => {
    if (!updateAvailable) return;

    if (pathAtDetection.current === null) {
      pathAtDetection.current = location.pathname;
      if (UPDATE_STRATEGY === 'force' && !blocked) reload();
      return;
    }

    if (UPDATE_STRATEGY === 'auto' && !blocked && location.pathname !== pathAtDetection.current) {
      reload();
    }
  }, [updateAvailable, location.pathname, blocked]);

  // Al volver a la pestaña después de un rato: revisa y recarga si ya hay build nuevo.
  useEffect(() => {
    if (UPDATE_STRATEGY === 'prompt') return;

    const onVisibility = async () => {
      if (document.visibilityState === 'hidden') {
        hiddenSince.current = Date.now();
        return;
      }
      const away = hiddenSince.current ? Date.now() - hiddenSince.current : 0;
      hiddenSince.current = null;
      if (away < IDLE_RELOAD_MS || blocked) return;
      if (await check()) reload();
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [check, blocked]);

  if (!updateAvailable || dismissed || UPDATE_STRATEGY === 'force') return null;

  return (
    <div className="psycle-update-banner" role="status" aria-live="polite">
      <span className="psycle-update-text">
        Hay una versión nueva de Psycle
        {latestVersion ? ` (v${latestVersion})` : ''}. Actualiza para ver los últimos cambios.
      </span>
      <div className="psycle-update-actions">
        <button type="button" className="psycle-update-btn" onClick={reload}>
          Actualizar ahora
        </button>
        <button
          type="button"
          className="psycle-update-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Cerrar aviso"
        >
          ✕
        </button>
      </div>
    </div>
  );
}